const PRESET_THEMES = {
  default: {
    label: "Default Blue",
    color: "#0055FF",
    sidebar: "#F8FAFC",
    pageBg: "#FFFFFF",
    pageFg: "#0A0A0A",
    fieldBg: "#FFFFFF",
    fieldBorder: "#E5E5E5",
  },
  ocean: {
    label: "Ocean Teal",
    color: "#0891B2",
    sidebar: "#F0FDFA",
    pageBg: "#FAFFFE",
    pageFg: "#0A0A0A",
    fieldBg: "#F0FDFA",
    fieldBorder: "#99F6E4",
  },
  forest: {
    label: "Forest Green",
    color: "#16A34A",
    sidebar: "#F0FDF4",
    pageBg: "#FAFDFB",
    pageFg: "#0A0A0A",
    fieldBg: "#F0FDF4",
    fieldBorder: "#BBF7D0",
  },
  sunset: {
    label: "Sunset Orange",
    color: "#EA580C",
    sidebar: "#FFF7ED",
    pageBg: "#FFFCF9",
    pageFg: "#0A0A0A",
    fieldBg: "#FFF7ED",
    fieldBorder: "#FED7AA",
  },
  royal: {
    label: "Royal Purple",
    color: "#7C3AED",
    sidebar: "#F5F3FF",
    pageBg: "#FAFAFE",
    pageFg: "#0A0A0A",
    fieldBg: "#F5F3FF",
    fieldBorder: "#DDD6FE",
  },
  slate: {
    label: "Slate Grey",
    color: "#475569",
    sidebar: "#F1F5F9",
    pageBg: "#F8FAFC",
    pageFg: "#0F172A",
    fieldBg: "#F1F5F9",
    fieldBorder: "#CBD5E1",
  },
  crimson: {
    label: "Crimson Red",
    color: "#DC2626",
    sidebar: "#FEF2F2",
    pageBg: "#FFFBFB",
    pageFg: "#0A0A0A",
    fieldBg: "#FEF2F2",
    fieldBorder: "#FECACA",
  },
  midnight: {
    label: "Midnight",
    color: "#6366F1",
    sidebar: "#1E1B4B",
    pageBg: "#0F0D2E",
    pageFg: "#E0E7FF",
    fieldBg: "#1E1B4B",
    fieldBorder: "#3730A3",
  },
  charcoal: {
    label: "Charcoal",
    color: "#0055FF",
    sidebar: "#1F2937",
    pageBg: "#111827",
    pageFg: "#F3F4F6",
    fieldBg: "#1F2937",
    fieldBorder: "#374151",
  },
  navy: {
    label: "Navy",
    color: "#3B82F6",
    sidebar: "#172554",
    pageBg: "#0F1729",
    pageFg: "#DBEAFE",
    fieldBg: "#172554",
    fieldBorder: "#1E3A5F",
  },
};

function hexToHSL(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function isLight(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 128;
}

export function applyTheme(themeNameOrColor) {
  const preset = typeof themeNameOrColor === "string" && PRESET_THEMES[themeNameOrColor]
    ? PRESET_THEMES[themeNameOrColor]
    : PRESET_THEMES.default;

  const primaryColor = preset.color;
  const [h, s, l] = hexToHSL(primaryColor);
  const root = document.documentElement;
  const sidebarLight = isLight(preset.sidebar);
  const pageLight = isLight(preset.pageBg);

  // Primary color variants
  root.style.setProperty("--theme-primary", primaryColor);
  root.style.setProperty("--theme-primary-hover", `hsl(${h}, ${s}%, ${Math.max(l - 12, 10)}%)`);
  root.style.setProperty("--theme-primary-light", `hsl(${h}, ${s}%, 96%)`);
  root.style.setProperty("--theme-primary-muted", `hsl(${h}, ${Math.round(s * 0.4)}%, 92%)`);
  root.style.setProperty("--theme-primary-10", `hsla(${h}, ${s}%, ${l}%, 0.1)`);
  root.style.setProperty("--theme-primary-5", `hsla(${h}, ${s}%, ${l}%, 0.05)`);

  // Sidebar
  root.style.setProperty("--theme-sidebar-bg", preset.sidebar);
  root.style.setProperty("--theme-sidebar-border", sidebarLight ? `hsl(${h}, ${Math.round(s * 0.2)}%, 88%)` : `hsl(${h}, ${Math.round(s * 0.3)}%, 25%)`);
  root.style.setProperty("--theme-sidebar-text", sidebarLight ? "#525252" : "#CBD5E1");
  root.style.setProperty("--theme-sidebar-heading", sidebarLight ? "#A3A3A3" : "#64748B");
  root.style.setProperty("--theme-sidebar-active-bg", sidebarLight ? `color-mix(in srgb, ${primaryColor} 8%, transparent)` : `color-mix(in srgb, ${primaryColor} 20%, transparent)`);
  root.style.setProperty("--theme-sidebar-active-text", sidebarLight ? primaryColor : "#E0E7FF");

  // Page
  root.style.setProperty("--theme-page-bg", preset.pageBg);
  root.style.setProperty("--theme-page-fg", preset.pageFg);
  root.style.setProperty("--theme-page-muted", pageLight ? "#525252" : "#94A3B8");
  root.style.setProperty("--theme-page-subtle", pageLight ? "#A3A3A3" : "#64748B");

  // Fields & Cards
  root.style.setProperty("--theme-field-bg", preset.fieldBg);
  root.style.setProperty("--theme-field-border", preset.fieldBorder);
  root.style.setProperty("--theme-card-bg", pageLight ? "#FFFFFF" : preset.fieldBg);
  root.style.setProperty("--theme-card-border", preset.fieldBorder);

  // Accent
  root.style.setProperty("--theme-accent", `hsl(${h}, ${s}%, ${Math.max(l - 30, 8)}%)`);
}

export default PRESET_THEMES;
